/**
 * Helper class for making queries that depend on each other or for groups of
 * queries that all need to resolve before taking an action. Just cleans up
 * all of the '.then' statements needed for that kind of thing.
 */

export default class SynchronousQueryStream {
  constructor(apolloClient) {
    this.client = apolloClient;
    this.actions = [];
    this.catchHandlers = [];
    this.ctx = {};
  }

  /**
   * Basically a pass-through to the Apollo client.query
   */
  query = args => {
    this.actions.push(() => this.client.query(args));
    return this;
  };

  /**
   * Takes the result of the previous chained action and adds it to the collected
   * context
   */
  resolveToContext = () => {
    this.actions.push(data => {
      this.ctx = { ...this.ctx, ...data };
    });
    return this;
  };

  /**
   * Does basically the same thing as resolveToContext but destructures the
   * query result for you.
   */
  resolveQueryResultToContext = () => {
    this.actions.push(data => {
      this.ctx = { ...this.ctx, ...data.data };
    });
    return this;
  };

  /**
   * You get the result of the previous chained command and the context and the
   * result is transformed for the next chained command.
   */
  map = handler => {
    this.actions.push(handler);
    return this;
  };

  /**
   * You get the result of the previous chained command and the context but
   * this doesn't expect a return value and just passes the previous result
   * through to the next command.
   */
  peek = handler => {
    this.actions.push((...args) => {
      handler(...args);
      return args;
    });
    return this;
  };

  /**
   * This is a non-blocking catch that catches any error that has occurred since
   * the last catch. If this error isn't thrown, the return value of the handler
   * is passed to the next chained command after the catch.
   */
  catch = handler => {
    const startIdx = this.catchHandlers.length
      ? this.catchHandlers[this.catchHandlers.length - 1].range[0]
      : 0;
    this.catchHandlers.push({
      range: [startIdx, this.actions.length],
      handler
    });
    return this;
  };

  /**
   * The commands will not be executed unless this is put at the end. This is
   * the final command that executes the chain of commands. Takes a handler that
   * will be passed the final result value as well as the final context value.
   */
  collect = async handler => {
    let result;
    for (let i = 0; i < this.actions.length; i += 1) {
      try {
        // eslint-disable-next-line no-await-in-loop
        result = await this.actions[i](result, this.ctx);
      } catch (err) {
        let handled = false;
        for (let j = 0; j < this.catchHandlers.length; j += 1) {
          const catchHandler = this.catchHandlers[j];
          if (i >= catchHandler.range[0] && i < catchHandler.range[1]) {
            result = catchHandler.handler(err, result, this.ctx);
            handled = true;
            // eslint-disable-next-line prefer-destructuring
            i = catchHandler.range[1];
            break;
          }
        }
        if (!handled) throw err;
      }
    }

    handler(result, this.ctx);
  };
}
